// Web Push per la fine del recupero (iPhone PWA installata / Android / desktop).
// Gestisce: supporto/stato permesso, attivazione (SW + subscribe + salvataggio
// su Supabase), scheduling/annullamento della notifica per il recupero corrente.
//
// Il "programma" della notifica vive lato server (api/rest-push.js) e viene
// governato dalla riga Supabase rest_push_state: annullare/sostituire un
// recupero = cancellare/riscrivere quella riga. Vedi il commento nell'API.

import { supabase } from './supabase';
import { supabaseFetch } from './database/core';

// Chiave pubblica VAPID (è pubblica per definizione: può stare nel client).
// La PRIVATA vive SOLO nelle env di Vercel (vedi api/rest-push.js).
export const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_VAPID_PUBLIC_KEY ||
  'BNeW1Z-CT0uvaw988t6cxihHb94aLkez13CIiz7a8ciC2cmYI2KAYnpK5MwXW0V4HqY1dwuEJXeyaxdYDphx5js';

const ENABLED_FLAG = 'coachfit_push_enabled';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// PWA installata (obbligatoria su iOS per le push)
export const isStandalone = () =>
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

/** Stato sintetico per la UI: unsupported | need-install | denied | enabled | off */
export const getPushStatus = () => {
  if (!isPushSupported()) {
    return isIOS() && !isStandalone() ? 'need-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  let flag = null;
  try {
    flag = localStorage.getItem(ENABLED_FLAG);
  } catch (e) { /* storage bloccato: trattato come off */ }
  if (Notification.permission === 'granted' && flag === '1') return 'enabled';
  return 'off';
};

export const registerSW = async () => {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return reg;
};

/** Attiva le notifiche: DA CHIAMARE IN UN GESTURE HANDLER (richiesta permesso iOS). */
export const enablePush = async () => {
  const reg = await registerSW();
  if (!reg) throw new Error('Service worker non disponibile');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('permission-' + permission);

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
  const json = sub.toJSON();

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('Sessione non disponibile');

  await supabaseFetch('/push_subscriptions?on_conflict=endpoint', {
    method: 'POST',
    headers: { Prefer: 'resolution=merge-duplicates,return=representation' },
    body: JSON.stringify({
      user_id: session.user.id,
      endpoint: sub.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    }),
  });

  try {
    localStorage.setItem(ENABLED_FLAG, '1');
  } catch (e) { /* storage bloccato: lo stato si ricava comunque dal permesso */ }
  return true;
};

export const disablePush = async () => {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = await reg?.pushManager?.getSubscription();
    if (sub) {
      await supabaseFetch(`/push_subscriptions?endpoint=eq.${encodeURIComponent(sub.endpoint)}`, { method: 'DELETE' });
      await sub.unsubscribe();
    }
  } catch (e) { /* best effort */ }
  try {
    localStorage.removeItem(ENABLED_FLAG);
  } catch (e) { /* ignore */ }
};

const pushEnabled = () => getPushStatus() === 'enabled';

/**
 * Programma la notifica per la fine del recupero corrente.
 * Fire-and-forget: non blocca la UI; se fallisce resta il fallback in-app.
 */
export const schedulePushForRest = async (endTimeMs, timerId) => {
  if (!pushEnabled()) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // 1. Stato del recupero attivo (una riga per utente, upsert su PK)
    await supabaseFetch('/rest_push_state?on_conflict=user_id', {
      method: 'POST',
      headers: { Prefer: 'resolution=merge-duplicates,return=representation' },
      body: JSON.stringify({
        user_id: session.user.id,
        timer_id: timerId,
        end_time: new Date(endTimeMs).toISOString(),
      }),
    });

    // 2. Avvia lo scheduler serverless (fire-and-forget, keepalive per iOS)
    fetch('/api/rest-push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ timerId, endTime: endTimeMs, token: session.access_token }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    console.warn('[PUSH] schedule failed (fallback in-app resta attivo):', e.message);
  }
};

/** Annulla la notifica programmata (pausa/chiusura/fine in-app del recupero). */
export const cancelPushForRest = async () => {
  if (!pushEnabled()) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;
    // Cancellare la riga = le invocazioni server in attesa muoiono al check
    await supabaseFetch(`/rest_push_state?user_id=eq.${session.user.id}`, { method: 'DELETE' });
  } catch (e) { /* best effort */ }
};

/**
 * Legge lo stato del recupero server-side (PB-003). Ritorna:
 *   { enabled: false }                            → push non attive
 *   { enabled: true, row: null }                  → riga assente: il server
 *       l'ha consumata dopo un invio riuscito (vedi api/rest-push.js: la riga
 *       viene cancellata SOLO se almeno una push è partita)
 *   { enabled: true, row: { timer_id, end_time } } → recupero ancora "in carico"
 *   { enabled: true, error: true }                → lettura fallita (in dubbio)
 * Sola lettura: non scrive nulla.
 */
export const getRestPushState = async () => {
  if (!pushEnabled()) return { enabled: false, row: null };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return { enabled: false, row: null };
    const rows = await supabaseFetch(
      `/rest_push_state?user_id=eq.${session.user.id}&select=timer_id,end_time`
    );
    const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
    return { enabled: true, row };
  } catch (e) {
    return { enabled: true, row: null, error: true };
  }
};

/**
 * Decisore PURO (PB-003): la push server ha già coperto l'avviso di fine per
 * QUESTO timer? `true` → NON riprodurre il secondo avviso in-app.
 * - push non attive o errore di lettura → `false` (avvisa: nessuna regressione
 *   quando le push sono off o in caso di push fallita/lettura incerta);
 * - riga assente → server ha consumato dopo un invio riuscito → `true`;
 * - riga di un altro timer → il nostro recupero è superato/sostituito → `true`;
 * - riga del NOSTRO timer → il server non ha (ancora) inviato → `false` (avvisa).
 */
export const serverAlreadyCoveredRest = ({ enabled, row, error, savedTimerId }) => {
  if (!enabled || error) return false;
  const rowIsOurs = !!(row && savedTimerId && row.timer_id === savedTimerId);
  return !rowIsOurs;
};
